<template>
    <component :is="embedded ? 'div' : 'el-dialog'" v-bind="containerProps"
        @update:visible="handleDialogVisible">
        <div class="auto-msku-toolbar">
            <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="fetchTasks">
                刷新
            </el-button>
            <el-select v-model="ownerFilter" size="small" clearable filterable
                placeholder="按创建人筛选" :loading="usersLoading"
                class="auto-msku-user-filter" @change="fetchTasks">
                <el-option v-for="user in userOptions" :key="user.value"
                    :label="user.label" :value="user.value" />
            </el-select>
            <el-select v-model="sourceFilter" size="small" clearable
                placeholder="按创建方式筛选" class="auto-msku-source-filter" @change="fetchTasks">
                <el-option label="手动创建" value="manual" />
                <el-option label="自动刊登" value="auto" />
                <el-option label="历史任务" value="legacy" />
            </el-select>
            <el-input v-model="searchFilters.msku" size="small" clearable placeholder="搜索 MSKU"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.sku" size="small" clearable placeholder="搜索关联 SKU"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.pm" size="small" clearable placeholder="搜索品名"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.parent" size="small" clearable placeholder="搜索父体"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="fetchTasks">搜索</el-button>
            <el-button size="small" @click="resetSearch">重置</el-button>
            <span class="auto-msku-tip">支持包含匹配，多个条件同时生效。</span>
        </div>
        <div class="auto-msku-toolbar">
            <el-button size="small" type="warning" :loading="bulkLoading.pause"
                :disabled="!canPauseSelected" @click="runSelectedMonitorAction('pause')">
                暂停所选
            </el-button>
            <el-button size="small" type="primary" :loading="bulkLoading.resume"
                :disabled="!canResumeSelected" @click="runSelectedMonitorAction('resume')">
                恢复／重新监听所选
            </el-button>
            <el-button size="small" type="success" plain :loading="bulkLoading.retry_pair"
                :disabled="!canRetryPairSelected" @click="runSelectedMonitorAction('retry_pair')">
                重试配对
            </el-button>
            <el-button size="small" type="danger" plain :loading="bulkLoading.ignore"
                :disabled="!canIgnoreSelected" @click="runSelectedMonitorAction('ignore')">
                忽略所选
            </el-button>
            <el-button size="small" type="danger" :loading="bulkLoading.delete"
                :disabled="!canDeleteSelected" @click="runSelectedMonitorAction('delete')">
                删除所选
            </el-button>
            <span class="auto-msku-tip">
                新版手动创建任务首次在 30 分钟后查询产品，自动刊登成功后立即查询；刊登、产品同步和配对状态分开显示。
                暂停／忽略／删除会阻止后续动作，已发出的请求仍保留回执；只有明确刊登失败可从结果页修正并重试，只有配对失败可重试配对。
            </span>
        </div>

        <el-table ref="taskTable" class="auto-msku-task-table" :data="taskList" size="small" border stripe max-height="560"
            v-loading="loading" @selection-change="onSelectionChange">
            <el-table-column type="selection" width="46" :selectable="isSelectable" />
            <el-table-column label="创建方式" width="92" align="center">
                <template slot-scope="scope">{{ sourceText(scope.row) }}</template>
            </el-table-column>
            <el-table-column prop="parent" label="父体" min-width="130" show-overflow-tooltip />
            <el-table-column prop="pm" label="品名" min-width="140" show-overflow-tooltip />
            <el-table-column prop="shop" label="店铺" width="120" />
            <el-table-column prop="username" label="创建人" width="100" show-overflow-tooltip />
            <el-table-column prop="sku" label="关联SKU" min-width="130" show-overflow-tooltip />
            <el-table-column prop="msku" label="MSKU" min-width="230" show-overflow-tooltip />
            <el-table-column label="同步状态" width="125" align="center">
                <template slot-scope="scope">
                    <el-tag size="mini" :type="syncStatusType(scope.row)">
                        {{ scope.row.status === 'paired' ? '已配对' : (scope.row.status_msg || scope.row.sync_status || '未监控') }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column label="自动刊登" width="125" align="center">
                <template slot-scope="scope">
                    <el-tag v-if="scope.row.workflow_version === 2" size="mini"
                        :type="publishStatusType(scope.row.publish_status)">
                        {{ publishStatusText(scope.row.publish_status) }}
                    </el-tag>
                    <span v-else class="auto-msku-muted">—</span>
                </template>
            </el-table-column>
            <el-table-column label="是否配对" width="95" align="center">
                <template slot-scope="scope">
                    {{ scope.row.should_pair === null || scope.row.should_pair === undefined
                        ? '历史未知' : (scope.row.should_pair ? '是' : '否') }}
                </template>
            </el-table-column>
            <el-table-column label="配对结果" width="105" align="center">
                <template slot-scope="scope">
                    <span v-if="scope.row.workflow_version !== 2" class="auto-msku-muted">—</span>
                    <el-tag v-else size="mini" :type="pairStatusType(scope.row)">
                        {{ pairStatusText(scope.row) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column type="expand" width="42">
                <template slot-scope="scope">
                    <div class="auto-msku-history" v-if="(scope.row.publish_history || []).length"
                        :style="{ width: historyWidth + 'px' }">
                        <div class="auto-msku-history-title">刊登尝试历史（{{ scope.row.publish_history.length }}）</div>
                        <el-table :data="scope.row.publish_history" size="mini" border stripe>
                            <el-table-column prop="attempt_no" label="尝试" width="58" />
                            <el-table-column prop="task_id" label="刊登任务" min-width="300" class-name="history-text-cell" />
                            <el-table-column label="状态" width="110">
                                <template slot-scope="attempt">
                                    <el-tag size="mini" :type="publishStatusType(attempt.row.publish_status)">
                                        {{ publishStatusText(attempt.row.publish_status) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="record_unique_id" label="刊登批次" min-width="190" class-name="history-text-cell" />
                            <el-table-column prop="error" label="错误" min-width="240" class-name="history-text-cell" />
                        </el-table>
                    </div>
                    <span v-else class="auto-msku-muted">暂无刊登尝试（手动创建任务会直接等待产品同步）</span>
                </template>
            </el-table-column>
            <el-table-column prop="fnsku" label="FNSKU" min-width="130" show-overflow-tooltip />
            <el-table-column prop="asin" label="ASIN" width="125" show-overflow-tooltip />
            <el-table-column prop="local_sku" label="local_sku" min-width="130" show-overflow-tooltip />
            <el-table-column prop="created_at" label="创建时间" width="160" />
            <el-table-column prop="deadline_at" label="截止时间" width="160" />
            <el-table-column label="操作" width="250" fixed="right" align="center">
                <template slot-scope="scope">
                    <el-button v-if="isPublishRetryable(scope.row)" type="text" size="mini"
                        style="color:#E6A23C;" @click="openPublishRetry(scope.row)">
                        修正重试刊登
                    </el-button>
                    <el-button v-if="isRetryPairable(scope.row)" type="text" size="mini"
                        style="color:#67C23A;" :loading="scope.row._monitorAction === 'retry_pair'"
                        @click="runRowMonitorAction(scope.row, 'retry_pair')">
                        重试配对
                    </el-button>
                    <template v-if="isPauseable(scope.row)">
                        <el-button type="text" size="mini" :loading="scope.row._monitorAction === 'pause'"
                            @click="runRowMonitorAction(scope.row, 'pause')">
                            暂停
                        </el-button>
                    </template>
                    <el-button v-else-if="isResumable(scope.row)" type="text" size="mini"
                        :loading="scope.row._monitorAction === 'resume'"
                        @click="runRowMonitorAction(scope.row, 'resume')">
                        {{ scope.row.status === 'expired' ? '重新监听' : '恢复监控' }}
                    </el-button>
                    <el-button v-if="isIgnorable(scope.row)" type="text" size="mini" style="color:#F56C6C;"
                        :loading="scope.row._monitorAction === 'ignore'"
                        @click="runRowMonitorAction(scope.row, 'ignore')">忽略</el-button>
                    <el-button v-if="isSelectable(scope.row)" type="text" size="mini" style="color:#F56C6C;"
                        :loading="scope.row._monitorAction === 'delete'"
                        @click="runRowMonitorAction(scope.row, 'delete')">删除</el-button>
                </template>
            </el-table-column>
        </el-table>
        <div v-if="!loading && !taskList.length" class="auto-msku-empty">暂无符合条件的 Auto MSKU 记录</div>
        <el-dialog title="修正字段并重试刊登" :visible.sync="publishRetryVisible"
            width="560px" append-to-body :close-on-click-modal="false">
            <div class="auto-msku-retry-tip">
                沿用原 MSKU 和主任务，只提交下一次刊登尝试。请输入 JSON：可直接填写字段值，或使用
                <code>{"attributes": { ... }}</code>；历史提交快照不会被覆盖。
            </div>
            <el-input v-model="publishRetryJson" type="textarea" :rows="10"
                placeholder="例如：{&quot;brand&quot;:&quot;修正后的品牌&quot;}" />
            <span slot="footer">
                <el-button size="small" @click="publishRetryVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="publishRetryLoading"
                    @click="submitPublishRetry">确认重试</el-button>
            </span>
        </el-dialog>
    </component>
</template>

<script>
export default {
    name: 'AutoMskuTaskDialog',
    props: {
        visible: { type: Boolean, default: false },
        // Auto工作台内嵌时不再创建第二层 el-dialog。
        embedded: { type: Boolean, default: false },
        // 内嵌模式只在当前分页激活时加载，避免隐藏页签提前请求。
        active: { type: Boolean, default: true },
        // 从批量 ASIN 结果页打开时，按主任务的 MSKU 定位统一工作台记录。
        initialMsku: { type: String, default: '' },
    },
    data() {
        return {
            taskList: [],
            historyWidth: 900,
            selectedRows: [],
            loading: false,
            usersLoading: false,
            userOptions: [],
            ownerFilter: '',
            sourceFilter: '',
            searchFilters: { msku: '', sku: '', pm: '', parent: '' },
            taskRequestId: 0,
            bulkLoading: { pause: false, resume: false, retry_pair: false, ignore: false, delete: false },
            publishRetryVisible: false,
            publishRetryLoading: false,
            publishRetryRow: null,
            publishRetryJson: '{}',
            publishRetryRequestId: '',
            appliedInitialMsku: null,
        };
    },
    computed: {
        containerProps() {
            if (this.embedded) return {};
            return {
                title: 'Auto MSKU记录',
                visible: this.visible,
                width: '94%',
                closeOnClickModal: false,
                appendToBody: true,
            };
        },
        visibleSync: {
            get() { return this.visible; },
            set(value) { this.$emit('update:visible', value); },
        },
        canPauseSelected() {
            return this.selectedRows.some(row => this.isPauseable(row));
        },
        canResumeSelected() {
            return this.selectedRows.some(row => this.isResumable(row));
        },
        canRetryPairSelected() {
            return this.selectedRows.some(row => this.isRetryPairable(row));
        },
        canIgnoreSelected() {
            return this.selectedRows.some(row => this.isIgnorable(row));
        },
        canDeleteSelected() {
            return this.selectedRows.some(row => this.isSelectable(row));
        },
    },
    watch: {
        visible(value) {
            if (value && (!this.embedded || this.active)) {
                this.applyInitialMsku(true);
                this.fetchTasks();
            }
        },
        active(value) {
            if (!this.embedded) return;
            if (value && this.visible) {
                this.applyInitialMsku(true);
                this.fetchTasks();
            }
        },
        initialMsku() {
            if (!this.visible || (this.embedded && !this.active)) return;
            this.applyInitialMsku(true);
            this.fetchTasks();
        },
    },
    mounted() {
        this.$nextTick(() => {
            const table = this.$refs.taskTable;
            if (!table) return;
            this.historyResizeObserver = new ResizeObserver(entries => {
                const width = entries[0].contentRect.width;
                if (width > 0) this.historyWidth = Math.max(240, width - 250 - 50);
            });
            this.historyResizeObserver.observe(table.$el);
        });
        if (this.visible && (!this.embedded || this.active)) {
            this.fetchTasks();
        }
    },
    beforeDestroy() {
        if (this.historyResizeObserver) this.historyResizeObserver.disconnect();
    },
    methods: {
        applyInitialMsku(force) {
            const value = String(this.initialMsku || '').trim();
            if (!force && this.appliedInitialMsku === value) return;
            this.searchFilters = { ...this.searchFilters, msku: value };
            this.appliedInitialMsku = value;
        },
        handleDialogVisible(value) {
            if (!this.embedded) this.visibleSync = value;
        },
        syncStatusType(row) {
            // 「自动刊登」和「同步状态」是两个阶段：刊登成功后才进入产品同步。
            // 产品同步报错（ERP 超时/限流重试）必须显红，否则和正常「等待产品」看不出区别。
            const text = String((row && (row.status_msg || row.sync_status)) || '');
            if (text.includes('失败') || text.includes('超时')) return 'danger';
            return this.statusType((row || {}).status);
        },
        statusType(status) {
            const types = {
                generated: 'info', created_only: 'info', waiting_publish: 'warning', publish_failed: 'danger',
                waiting_product: 'warning', waiting_fnsku: 'warning',
                waiting_pair: 'danger', waiting_local_sku: 'warning', paired: 'success',
                expired: 'danger', manual_review: 'danger', paused: 'info', ignored: 'info',
            };
            return types[status] || 'info';
        },
        sourceText(row) {
            if (!row || row.source === 'legacy' || row.workflow_version !== 2) return '历史任务';
            return row.creation_source === 'auto' ? '自动刊登' : '手动创建';
        },
        publishStatusText(status) {
            const labels = {
                not_started: '未开始', pending: '待提交', submitting: '提交中',
                publishing: '刊登中', success: '刊登成功', explicit_failed: '明确失败',
                result_unknown: '结果待确认',
            };
            return labels[status] || (status || '未开始');
        },
        publishStatusType(status) {
            if (status === 'success') return 'success';
            if (status === 'explicit_failed' || status === 'result_unknown') return 'danger';
            if (['publishing', 'submitting', 'pending'].includes(status)) return 'warning';
            return 'info';
        },
        pairStatusText(row) {
            if (!row || row.should_pair === null || row.should_pair === undefined) return '历史未知';
            if (!row.should_pair) return '不需要';
            if (row.status === 'paired' || row.pair_status === 'success') return '成功';
            if (row.status === 'waiting_pair' || row.pair_status === 'waiting') return '待重试';
            if (row.status === 'manual_review') return '待核查';
            return '等待';
        },
        pairStatusType(row) {
            const status = this.pairStatusText(row);
            if (status === '成功') return 'success';
            if (status === '待重试' || status === '待核查') return 'danger';
            if (status === '不需要' || status === '历史未知') return 'info';
            return 'warning';
        },
        isPublishRetryable(row) {
            return !!row && row.workflow_version === 2
                && row.publish_status === 'explicit_failed'
                && !!row.publish_task_id && !!row.id;
        },
        isSelectable(row) {
            return !!row && !!row.id && !['paired', 'deleted'].includes(row.status);
        },
        isPauseable(row) {
            return this.isSelectable(row)
                && ['waiting_publish', 'waiting_product', 'waiting_fnsku', 'waiting_pair',
                    'waiting_local_sku'].includes(row.status);
        },
        isResumable(row) {
            return this.isSelectable(row)
                && ['paused', 'ignored', 'generated', 'expired'].includes(row.status);
        },
        isRetryPairable(row) {
            return !!row && row.status === 'waiting_pair';
        },
        openPublishRetry(row) {
            this.publishRetryRow = row;
            const current = (row.publish_history || []).find(item => item.task_id === row.publish_task_id)
                || (row.publish_history || [])[0];
            const values = current && current.submitted_fields && current.submitted_fields.attributes
                ? current.submitted_fields.attributes : {};
            // 当前历史快照本身就是最终 attributes；保留外层标识，提交时
            // 后端不会把 Amazon 属性结构误当成未组装的普通字段值再次包装。
            this.publishRetryJson = JSON.stringify({ attributes: values }, null, 2);
            this.publishRetryRequestId = 'retry_' + Date.now() + '_' + Math.random().toString(16).slice(2);
            this.publishRetryVisible = true;
        },
        submitPublishRetry() {
            const row = this.publishRetryRow;
            if (!row) return;
            let corrected;
            try {
                corrected = JSON.parse(this.publishRetryJson || '{}');
            } catch (error) {
                this.$message.error('修正字段必须是合法 JSON');
                return;
            }
            this.publishRetryLoading = true;
            this.$axios.post('/vue/vue_product/batch-asin/retry', {
                master_task_id: row.id,
                failed_publish_task_id: row.publish_task_id,
                corrected_fields: corrected,
                request_id: this.publishRetryRequestId,
            }).then(res => {
                if (res.code !== 200) throw new Error(res.message || '刊登重试失败');
                this.$message.success('已进入刊登重试队列');
                this.publishRetryVisible = false;
                this.fetchTasks();
            }).catch(error => {
                this.$message.error((error && error.message) || '刊登重试失败');
            }).finally(() => { this.publishRetryLoading = false; });
        },
        isIgnorable(row) {
            return this.isPauseable(row) || (this.isSelectable(row) && ['paused', 'generated'].includes(row.status));
        },
        onSelectionChange(rows) {
            this.selectedRows = rows || [];
        },
        fetchUsers() {
            this.usersLoading = true;
            // 只展示已经创建过 Auto MSKU 记录的去重创建人，不读取全量账号列表。
            this.$axios.get('/vue/msku-generation/task-owners')
                .then(res => {
                    if (res.code === 200) {
                        this.userOptions = res.data || [];
                    } else {
                        this.$message.error(res.message || '创建人列表获取失败');
                    }
                })
                .catch(() => this.$message.error('创建人列表获取失败'))
                .finally(() => { this.usersLoading = false; });
        },
        resetSearch() {
            this.ownerFilter = '';
            this.sourceFilter = '';
            this.searchFilters = { msku: '', sku: '', pm: '', parent: '' };
            return this.fetchTasks();
        },
        fetchTasks() {
            const requestId = ++this.taskRequestId;
            this.loading = true;
            this.selectedRows = [];
            this.fetchUsers();
            const params = { limit: 300 };
            if (this.ownerFilter) params.owner_account = this.ownerFilter;
            if (this.sourceFilter) params.source = this.sourceFilter;
            Object.keys(this.searchFilters).forEach(field => {
                const value = this.searchFilters[field].trim();
                if (value) params[field] = value;
            });
            return this.$axios.get('/vue/msku-generation/tasks', { params })
                .then(res => {
                    if (requestId !== this.taskRequestId) return;
                    if (res.code === 200) {
                        this.taskList = (res.data || []).map(row => Object.assign({ _monitorAction: '' }, row));
                    } else {
                        this.$message.error(res.message || 'Auto MSKU 记录查询失败');
                    }
                })
                .catch(() => {
                    if (requestId === this.taskRequestId) this.$message.error('Auto MSKU 记录查询失败');
                })
                .finally(() => {
                    if (requestId === this.taskRequestId) this.loading = false;
                });
        },
        runRowMonitorAction(row, action) {
            if (!this.isSelectable(row)) return;
            if (action === 'delete') {
                this.confirmDelete([row.id], row);
                return;
            }
            if (action === 'retry_pair') {
                this.$confirm(
                    '将立即调用 ERP 配对方法。请先确认上次配对确实失败，且 ERP 中没有有效的旧配对关系，是否继续？',
                    '确认重试配对',
                    { confirmButtonText: '确认重试', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, [row.id], row)).catch(() => { });
                return;
            }
            if (action === 'ignore') {
                this.$confirm(
                    '忽略后停止轮询和超时通知，记录仍展示且可恢复监控，是否继续？',
                    '确认忽略监控',
                    { confirmButtonText: '确认忽略', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, [row.id], row)).catch(() => { });
                return;
            }
            this.runMonitorAction(action, [row.id], row);
        },
        runSelectedMonitorAction(action) {
            if (action === 'delete') {
                const ids = this.selectedRows.filter(row => this.isSelectable(row)).map(row => row.id);
                if (ids.length) this.confirmDelete(ids);
                return;
            }
            const canRun = action === 'pause' ? this.canPauseSelected
                : action === 'resume' ? this.canResumeSelected
                    : action === 'retry_pair' ? this.canRetryPairSelected : this.canIgnoreSelected;
            if (!canRun) return;
            const rows = this.selectedRows.filter(row => action === 'pause'
                ? this.isPauseable(row)
                : action === 'resume' ? this.isResumable(row)
                    : action === 'retry_pair' ? this.isRetryPairable(row) : this.isIgnorable(row));
            if (!rows.length) return;
            const ids = rows.map(row => row.id);
            if (action === 'retry_pair') {
                this.$confirm(
                    '将立即为选中的 ' + ids.length + ' 条记录调用 ERP 配对方法。请先确认这些记录配对确实失败，且 ERP 中没有有效的旧配对关系，是否继续？',
                    '确认重试配对',
                    { confirmButtonText: '确认重试', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, ids)).catch(() => { });
                return;
            }
            if (action === 'ignore') {
                this.$confirm(
                    '忽略后选中的 ' + ids.length + ' 条 MSKU 停止轮询和超时通知，记录仍展示且可恢复，是否继续？',
                    '确认忽略监控',
                    { confirmButtonText: '确认忽略', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, ids)).catch(() => { });
                return;
            }
            this.runMonitorAction(action, ids);
        },
        confirmDelete(ids, row) {
            this.$confirm(
                '删除这 ' + ids.length + ' 条 MSKU 记录后，将从工作台隐藏并停止后续轮询，无法在工作台恢复。不会删除 ERP 商品或解除配对；已经发出的 ERP 请求可能仍会完成。是否继续？',
                '确认删除记录',
                { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' }
            ).then(() => this.runMonitorAction('delete', ids, row)).catch(() => { });
        },
        runMonitorAction(action, ids, row) {
            const endpoints = {
                pause: '/vue/msku-generation/monitor/pause',
                resume: '/vue/msku-generation/monitor/resume',
                retry_pair: '/vue/msku-generation/monitor/retry-pair',
                ignore: '/vue/msku-generation/monitor/ignore',
                delete: '/vue/msku-generation/monitor/delete',
            };
            if (!endpoints[action] || !ids.length) return;
            if (row) this.$set(row, '_monitorAction', action);
            else this.$set(this.bulkLoading, action, true);
            const labels = {
                pause: '暂停监控', resume: '恢复／重新监听', retry_pair: '重试配对', ignore: '忽略监控', delete: '删除记录',
            };
            this.$axios.post(endpoints[action], { task_ids: ids })
                .then(res => {
                    if (res.code === 200) {
                        if (action === 'retry_pair' && res.data && res.data.outcomes) {
                            // 新逻辑配对接口成功即完成；兼容旧后端返回的 waiting_local_sku 统计。
                            const paired = res.data.outcomes.paired
                                || res.data.outcomes.waiting_local_sku || 0;
                            const failed = res.data.outcomes.waiting_pair || 0;
                            if (failed) {
                                this.$message.warning(
                                    '已执行重试：' + paired + ' 条配对成功，' + failed + ' 条仍然失败'
                                );
                            } else {
                                this.$message.success(
                                    '配对方法调用成功，共 ' + paired + ' 条已配对'
                                );
                            }
                        } else {
                            this.$message.success(labels[action] + '成功');
                        }
                        this.fetchTasks();
                    } else {
                        this.$message.error(res.message || labels[action] + '失败');
                    }
                })
                .catch(() => this.$message.error(labels[action] + '失败'))
                .finally(() => {
                    if (row) this.$set(row, '_monitorAction', '');
                    else this.$set(this.bulkLoading, action, false);
                });
        },
    },
};
</script>

<style scoped>
.auto-msku-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.auto-msku-user-filter {
    width: 180px;
}

.auto-msku-source-filter {
    width: 140px;
}

.auto-msku-search-input {
    width: 190px;
}

.auto-msku-tip {
    font-size: 12px;
    color: #909399;
}

.auto-msku-empty {
    padding: 18px 0;
    text-align: center;
    color: #909399;
    font-size: 13px;
}

.auto-msku-history {
    position: sticky;
    left: 16px;
    box-sizing: border-box;
    padding: 4px 0 8px;
    margin-left: 16px;
}

.auto-msku-task-table /deep/ .el-table__expanded-cell {
    padding: 16px 0;
}

.auto-msku-history /deep/ .history-text-cell .cell {
    white-space: normal;
    overflow-wrap: anywhere;
}

.auto-msku-history-title {
    margin-bottom: 6px;
    color: #606266;
    font-size: 12px;
}

/* 固定列会复制展开行；隐藏副本内容并保留行高，避免历史表格穿入操作区。 */
.auto-msku-task-table /deep/ .el-table__fixed-right .el-table__expanded-cell {
    background: #fff;
}

.auto-msku-task-table /deep/ .el-table__fixed-right .el-table__expanded-cell > * {
    visibility: hidden;
    pointer-events: none;
}

.auto-msku-muted {
    color: #c0c4cc;
}
</style>
